import { z } from "zod";

// ===== Finance: expenses + profit/loss report =====

export const expenseCategorySchema = z.enum([
	"rent",
	"salaries",
	"supplies",
	"utilities",
	"marketing",
	"equipment",
	"maintenance",
	"taxes",
	"other",
]);
export type ExpenseCategory = z.infer<typeof expenseCategorySchema>;

export const clinicExpenseSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	category: expenseCategorySchema,
	amount: z.number(),
	currency: z.string(),
	note: z.string().nullable(),
	spent_at: z.string(),
	recorded_by_member_id: z.string().uuid().nullable(),
	created_at: z.string(),
});
export type ClinicExpenseApi = z.infer<typeof clinicExpenseSchema>;

export const createExpenseInputSchema = z.object({
	category: expenseCategorySchema,
	amount: z.number().positive().max(1_000_000_000),
	note: z.string().max(500).nullable().optional(),
	/** ISO date; defaults to now server-side when omitted. */
	spent_at: z.string().datetime().optional(),
});
export type CreateExpenseInput = z.infer<typeof createExpenseInputSchema>;

export const updateExpenseInputSchema = createExpenseInputSchema.partial();
export type UpdateExpenseInput = z.infer<typeof updateExpenseInputSchema>;

// ===== Recurring expenses (templates) =====
// Rent, subscriptions, a monthly retainer — the owner defines them once and a
// daily cron materialises each into a real clinic_expenses row when its day of
// the month arrives, so they flow into the same P&L as one-off expenses. Day is
// capped at 28 so it lands every month (no Feb/short-month surprises).

export const recurringExpenseSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	category: expenseCategorySchema,
	amount: z.number(),
	currency: z.string(),
	day_of_month: z.number().int(),
	label: z.string().nullable(),
	is_active: z.boolean(),
	created_at: z.string(),
});
export type RecurringExpenseApi = z.infer<typeof recurringExpenseSchema>;

export const createRecurringExpenseInputSchema = z.object({
	category: expenseCategorySchema,
	amount: z.number().positive().max(1_000_000_000),
	day_of_month: z.number().int().min(1).max(28),
	label: z.string().max(200).nullable().optional(),
	is_active: z.boolean().optional(),
});
export type CreateRecurringExpenseInput = z.infer<
	typeof createRecurringExpenseInputSchema
>;

export const updateRecurringExpenseInputSchema =
	createRecurringExpenseInputSchema.partial();
export type UpdateRecurringExpenseInput = z.infer<
	typeof updateRecurringExpenseInputSchema
>;

// ===== Unified revenue across every department of a facility =====
// A facility's money lands in several places: consultation invoices (payments),
// the pharmacy POS (pharmacy_sales), the lab + radiology order prices. The
// finance report used to count ONLY consultation payments, so a clinic with a
// pharmacy/lab saw a P&L that silently omitted most of its income. These sources
// unify them, cash-basis, each tagged so the owner sees the split.
export const revenueSourceSchema = z.enum([
	"consultations",
	"pharmacy",
	"laboratory",
	"radiology",
]);
export type RevenueSource = z.infer<typeof revenueSourceSchema>;

export const revenueBySourceSchema = z.object({
	source: revenueSourceSchema,
	amount: z.number(),
});
export type RevenueBySource = z.infer<typeof revenueBySourceSchema>;

export const financeReportQuerySchema = z.object({
	from: z.string().datetime().optional(),
	to: z.string().datetime().optional(),
});
export type FinanceReportQuery = z.infer<typeof financeReportQuerySchema>;

export const financeReportResponseSchema = z.object({
	from: z.string(),
	to: z.string(),
	revenue: z.object({
		/** Cash collected across ALL departments in the window (cash-basis). */
		collected: z.number(),
		paid_invoices: z.number(),
		/** Issued + partially-paid invoice totals still owed. */
		outstanding: z.number(),
		/** Collected revenue split by department (consultations/pharmacy/lab/radiology). */
		by_source: z.array(revenueBySourceSchema),
	}),
	expenses: z.object({
		total: z.number(),
		by_category: z.array(
			z.object({ category: expenseCategorySchema, amount: z.number() }),
		),
	}),
	/** collected − expenses.total. */
	net: z.number(),
	/** Per-day revenue vs expenses for the trend chart. */
	by_day: z.array(
		z.object({ date: z.string(), revenue: z.number(), expenses: z.number() }),
	),
	currency: z.string(),
});
export type FinanceReportResponse = z.infer<typeof financeReportResponseSchema>;
